<template>
  <v-row>
    <v-row>
      <v-col class="align-left">
        <ExpandCollapseToggle
          id="bearingsPanelToggle"
          @toggleAllPanels="toggleAllPanels"
          styleClass="mt-3"
          :panel="panel"
        />
      </v-col>
      <v-col class="align-right mt-2">
        <ValidationCountsHeader v-if="moduleType == MODULE_TYPES.MANAGEMENT" />
      </v-col>
    </v-row>
    <v-expansion-panels v-model="panel" multiple color="#fff">
      <v-expansion-panel class="mt-5">
        <v-expansion-panel-title
          class="d-flex justify-start"
          hide-actions
          expand-icon="fa-solid fa-plus"
          collapse-icon="fa-solid fa-minus"
        >
          <v-icon v-if="!panel.includes(0)" icon="fa-solid fa-plus" />
          <v-icon v-if="panel.includes(0)" icon="fa-solid fa-minus" />
          <h3>Bearing Conditions</h3>
        </v-expansion-panel-title>

        <v-expansion-panel-text eager>
          <v-form ref="bearingConditions" v-model="valid">
            <LabelSelect
              :labelXl="3"
              :labelLg="4"
              :labelmd="4"
              :labelSm="5"
              :selectXl="3"
              :selectLg="4"
              :selectMd="5"
              :selectSm="6"
              id="bearingRating"
              label="B.C.07 Overall Bearing Condition Rating"
              :refTable="REFERENCE_TABLE.BEARING_CONDITION_RATING"
              :isEditable="isEditable"
              :isRequired="isEditable"
              v-model="bearingsValue.overallBearingCondRating"
            ></LabelSelect>
            <v-row>
              <v-col sm="12">
                <CommentTypeTextField
                  v-if="moduleType == MODULE_TYPES.INSPECTION"
                  label="Overall Bearing Notes"
                  id="field_Overall_Bearing_Notes"
                  :inspectionCommentType="INSPECTION_COMMENTS.BEARING_OVERALL_COMMENTS"
                  :readonly="!isEditable"
                  :counter="inspCommentMaxLength"
                  :maxlength="inspCommentMaxLength"
                  @changesDetected="inspectionStore.setDirtyFlag(true)"
                />
                <ManagementCommentTypeTextField
                  v-else
                  label="Overall Bearing Notes"
                  id="field_Overall_Bearing_Notes"
                  :inspectionCommentType="INSPECTION_COMMENTS.BEARING_OVERALL_COMMENTS"
                  :readonly="!isEditable"
                  :counter="inspCommentMaxLength"
                  :maxlength="inspCommentMaxLength"
                  :comments="bearingsValue.inspectionComments"
                  :brkey="bearingsValue.brkey"
                  :inspectionId="bearingsValue.inspectionId"
                  @updateComment="(comment) => setUpdatedComment(comment, INSPECTION_COMMENTS.BEARING_OVERALL_COMMENTS)"
                />
              </v-col>
            </v-row>
            <div style="display: flex">
              <v-btn
                id="btn_addNewBearing"
                variant="outlined"
                class="my-2"
                @click="addNewBearing"
                :disabled="!isEditable || anyBearingDetailEditable"
                >Add New Bearing</v-btn
              >
              <div style="display: flex">
                <v-checkbox
                  class="pl-6 pt-2 d-inline showAllCheckbox"
                  id="checkbox_SHOW_REPLACEMENT_BEARINGS"
                  hide-details
                  :disabled="anyBearingDetailEditable || !hasReplacedBearingDetails()"
                  :modelValue="showAllBearings"
                  @update:modelValue="
                    ($event) => {
                      showAllBearings = $event;
                      if (showAllBearings) {
                        getAllBearingDetails();
                      } else {
                        getActiveBearingDetails();
                      }
                    }
                  "
                >
                </v-checkbox>
                <span class="pl-2 d-inline font-weight-bold" style="padding-top: 16px">Show Replaced Bearings</span>
              </div>
            </div>
            <v-row class="mb-8">
              <v-col sm="12">
                <v-data-table
                  id="bearings-data-table"
                  v-model:page="pageInspection.page"
                  :items-per-page="pageInspection.itemsPerPage"
                  v-model:expanded="expandedRows"
                  :headers="headers"
                  :items="bearings"
                  class="compactTable lessPaddingTableColumns"
                  v-model:sort-by="sortBy"
                  hide-default-footer
                  must-sort
                  show-expand
                  return-object
                  @update:sort-by="bearingsFooter.setPageValue(1)"
                >
                  <template v-slot:[`header.data-table-expand`]="{ column }">
                    {{ column.title }}
                  </template>
                  <template v-slot:[`header.bearingId`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.recordKey`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.bearingType`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.bearingLocation`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.bearingCount`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.bearingMovement`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.corrosion`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.alignmentIssues`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.anchorBolt`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.bearingArea`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.conditionRating`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>
                  <template v-slot:[`header.action`]="{ column }">
                    <DataTableHeader :column="column" :sortBy="sortBy" />
                  </template>

                  <template v-slot:item="{ item }">
                    <tr>
                      <td :class="rowClass(item) + ' alignCenter'">
                        <template v-if="!anyBearingDetailEditable">
                          <button
                            v-if="!expandedRows.includes(item)"
                            @click="toggleRow(item)"
                            :id="`button_Chevron_Right_${item.uniqueKey}`"
                            :aria-describedby="`button_Chevron_Right_${item.uniqueKey}`"
                            :aria-label="`button_Chevron_Right_${item.uniqueKey}`"
                          >
                            <v-icon
                              :id="`icon_Chevron_Right_${item.recordKey}`"
                              icon="fa-solid fa-chevron-right"
                            ></v-icon>
                          </button>

                          <button
                            v-if="expandedRows.includes(item)"
                            @click="toggleRow(item)"
                            :id="`button_Chevron_Down_${item.uniqueKey}`"
                            :aria-describedby="`button_Chevron_Down_${item.uniqueKey}`"
                            :aria-label="`button_Chevron_Down_${item.uniqueKey}`"
                          >
                            <v-icon
                              :id="`icon_Chevron_Down_${item.uniqueKey}`"
                              icon="fa-solid fa-chevron-down"
                            ></v-icon>
                          </button>
                        </template>
                      </td>
                      <td :class="rowClass(item) + ' alignCenter'">
                        {{ item.bearingId }}
                      </td>
                      <td :class="rowClass(item) + ' alignCenter'">
                        {{ item.recordKey }}
                      </td>

                      <td :class="rowClass(item) + ' alignCenter'">
                        <div class="ellipsisText">
                          <span>{{
                            item?.bearingType
                              ? configStore.getReferenceValue(REFERENCE_TABLE.BEARING_TYPE, item?.bearingType)
                              : ""
                          }}</span>
                        </div>
                      </td>

                      <td :class="rowClass(item) + ' alignCenter'">
                        <div class="ellipsisText">
                          <span>
                            {{ getBearingLocation(item) }}
                          </span>
                        </div>
                      </td>

                      <td :class="rowClass(item) + ' alignCenter'">
                        <div class="ellipsisText">
                          <span>
                            {{ item.bearingCount }}
                          </span>
                        </div>
                      </td>

                      <td :class="rowClass(item) + ' alignCenter'">
                        <div class="ellipsisText">
                          <span>
                            {{
                              item?.bearingMovement
                                ? configStore.getReferenceValue(REFERENCE_TABLE.BEARING_MOVEMENT, item?.bearingMovement)
                                : ""
                            }}
                          </span>
                        </div>
                      </td>
                      <td :class="rowClass(item) + ' alignCenter'">
                        <div class="ellipsisText">
                          <span>
                            {{
                              item?.corrosion
                                ? configStore.getReferenceValue(REFERENCE_TABLE.BEARING_CORROSION, item?.corrosion)
                                : ""
                            }}
                          </span>
                        </div>
                      </td>
                      <td :class="rowClass(item) + ' alignCenter'">
                        <div class="ellipsisText">
                          <span>
                            {{
                              item?.alignmentIssues
                                ? configStore.getReferenceValue(
                                    REFERENCE_TABLE.BEARING_ALIGNMENT_ISSUES,
                                    item?.alignmentIssues
                                  )
                                : ""
                            }}
                          </span>
                        </div>
                      </td>
                      <td :class="rowClass(item) + ' alignCenter'">
                        <div class="ellipsisText">
                          <span>
                            {{
                              item?.anchorBolt
                                ? configStore.getReferenceValue(REFERENCE_TABLE.BEARING_ANCHOR_BOLT, item?.anchorBolt)
                                : ""
                            }}
                          </span>
                        </div>
                      </td>
                      <td :class="rowClass(item) + ' alignCenter'">
                        <div class="ellipsisText">
                          <span>
                            {{
                              item?.bearingArea
                                ? configStore.getReferenceValue(REFERENCE_TABLE.BEARING_LOSS_AREA, item?.bearingArea)
                                : ""
                            }}
                          </span>
                        </div>
                      </td>
                      <td :class="rowClass(item) + ' alignCenter'">
                        <div class="ellipsisText">
                          <span>
                            {{
                              item?.conditionRating
                                ? configStore.getReferenceValue(
                                    REFERENCE_TABLE.BEARING_CONDITION_RATING,
                                    item?.conditionRating
                                  )
                                : ""
                            }}
                          </span>
                        </div>
                      </td>

                      <td :class="rowClass(item) + ' alignCenter'" v-if="isEditable">
                        <div
                          style="white-space: nowrap"
                          v-if="isExpanded(item) && anyBearingDetailEditable && isEditable"
                        >
                          <IconTooltip
                            :id="`icon_add_${item.uniqueKey}`"
                            icon="fa-solid fa-check"
                            :tooltip="item?.isAdd ? 'Add' : 'Update'"
                            @click="saveDetail(item)"
                          />
                          <IconTooltip
                            :id="`icon_close_${item.uniqueKey}`"
                            icon="fa-solid fa-xmark"
                            tooltip="Close"
                            @click="closeDetail"
                          />
                        </div>
                        <div style="white-space: nowrap" v-else-if="isEditable && !anyBearingDetailEditable">
                          <IconTooltip
                            :id="`icon_edit_${item.uniqueKey}`"
                            icon="fa-solid fa-pencil"
                            tooltip="Edit"
                            @click="editDetail(item)"
                            :disabled="isNotCurrent(item)"
                          />
                          <IconTooltip
                            :id="`icon_replace_${item.uniqueKey}`"
                            icon="fa-solid fa-arrows-rotate"
                            tooltip="Replace"
                            @click="replaceDetail(item)"
                            :disabled="isNotCurrent(item)"
                          />
                          <IconTooltip
                            :id="`icon_delete_${item.uniqueKey}`"
                            icon="fa-solid fa-trash"
                            tooltip="Delete"
                            @click="deleteDetail(item)"
                            :disabled="isNotCurrent(item)"
                          />
                        </div>
                      </td>
                    </tr>
                  </template>

                  <template v-slot:expanded-row="{ item }">
                    <td :colspan="headers.length">
                      <v-form ref="detailForm" v-model="validDetails" class="mt-2" v-if="$vuetify.display.lgAndUp">
                        <v-row>
                          <v-col lg="6" xl="6" class="pa-0">
                            <LabelText
                              :labelXl="4"
                              :labelLg="6"
                              :textXl="7"
                              :textLg="5"
                              id="bearingId"
                              label="BRG.01 Bearing Key"
                              v-model="item.bearingId"
                              @update:modelValue="changesDetected = true"
                            >
                            </LabelText>
                          </v-col>
                          <v-col lg="6" xl="6" class="pa-0">
                            <LabelSelect
                              :labelXl="4"
                              :labelLg="6"
                              :selectXl="7"
                              :selectLg="5"
                              id="corrosion"
                              label="BRG.07 Corrosion?"
                              :showColon="false"
                              :refTable="REFERENCE_TABLE.BEARING_CORROSION"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.corrosion"
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col lg="6" xl="6" class="pa-0">
                            <LabelText
                              :labelXl="4"
                              :labelLg="6"
                              :textXl="7"
                              :textLg="5"
                              id="recordKey"
                              label="BRG.02 Record Key"
                              v-model="item.recordKey"
                              @update:modelValue="changesDetected = true"
                            >
                            </LabelText>
                          </v-col>
                          <v-col lg="6" xl="6" class="pa-0">
                            <LabelSelect
                              :labelXl="4"
                              :labelLg="6"
                              :selectXl="7"
                              :selectLg="5"
                              id="alignIssues"
                              label="BRG.08 Alignment Issues?"
                              :showColon="false"
                              :refTable="REFERENCE_TABLE.BEARING_ALIGNMENT_ISSUES"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.alignmentIssues"
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col lg="6" xl="6" class="pa-0">
                            <LabelSelect
                              :labelXl="4"
                              :labelLg="6"
                              :selectXl="7"
                              :selectLg="5"
                              id="bearingType"
                              label="BRG.03 Bearing Type"
                              :refTable="REFERENCE_TABLE.BEARING_TYPE"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.bearingType"
                              isRequired
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                          <v-col lg="6" xl="6" class="pa-0">
                            <LabelSelect
                              :labelXl="4"
                              :labelLg="6"
                              :selectXl="7"
                              :selectLg="5"
                              id="boltIssues"
                              label="BRG.09 Anchor Bolt Issues?"
                              :showColon="false"
                              :refTable="REFERENCE_TABLE.BEARING_ANCHOR_BOLT"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.anchorBolt"
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col lg="6" xl="6" class="pa-0">
                            <v-row
                              ><v-col lg="9" xl="8" class="pa-0">
                                <LabelSelect
                                  :labelXl="6"
                                  :labelLg="8"
                                  :selectXl="6"
                                  :selectLg="4"
                                  id="bearingLocation"
                                  label="BRG.04 Bearing Location"
                                  :options="bearingsValue.bearingLocations"
                                  :isEditable="
                                    (isEditable && !item.readOnly && !item.isAdd && !isReplacement(item)) ||
                                    (isEditable && item.isAdd && !item.isReplacing)
                                  "
                                  v-model="item.bearingLocation"
                                  isRequired
                                  @update:modelValue="changesDetected = true"
                                ></LabelSelect>
                              </v-col>
                              <v-col lg="3" xl="4" class="pa-0">
                                <LabelSelect
                                  :selectXl="9"
                                  :selectLg="8"
                                  id="bearingSubUnit"
                                  label=""
                                  :refTable="REFERENCE_TABLE.BEARING_SUBSTRUCTURE_UNIT"
                                  :isEditable="
                                    (isEditable && !item.readOnly && !item.isAdd && !isReplacement(item)) ||
                                    (isEditable && item.isAdd && !item.isReplacing)
                                  "
                                  v-model="item.bearingSubstrUnit"
                                  isRequired
                                  @update:modelValue="changesDetected = true"
                                ></LabelSelect>
                              </v-col>
                            </v-row>
                          </v-col>
                          <v-col lg="6" xl="6" class="pa-0">
                            <LabelSelect
                              :labelXl="4"
                              :labelLg="6"
                              :selectXl="7"
                              :selectLg="5"
                              id="bearingAreaLoss"
                              label="BRG.10 Loss of Bearing Area?"
                              :showColon="false"
                              :refTable="REFERENCE_TABLE.BEARING_LOSS_AREA"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.bearingArea"
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col lg="6" xl="6" class="pa-0">
                            <LabelText
                              :labelXl="4"
                              :labelLg="6"
                              :textXl="7"
                              :textLg="5"
                              id="bearingCount"
                              label="BRG.05 Bearing Count"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.bearingCount"
                              :isRequired="isEditable && !item.readOnly"
                              type="number"
                              maxlength="3"
                              @update:modelValue="changesDetected = true"
                              :rules="
                                isEditable
                                  ? [
                                      (value) => {
                                        return valueInRangeRule(value, 1, 999);
                                      },
                                    ]
                                  : []
                              "
                            ></LabelText>
                          </v-col>
                          <v-col lg="6" xl="6" class="pa-0">
                            <LabelSelect
                              :labelXl="4"
                              :labelLg="6"
                              :selectXl="7"
                              :selectLg="5"
                              id="bearingCondRating"
                              label="BRG.11 Condition Rating"
                              :refTable="REFERENCE_TABLE.BEARING_CONDITION_RATING"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.conditionRating"
                              isRequired
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col lg="6" xl="6" class="pa-0">
                            <LabelSelect
                              :labelXl="4"
                              :labelLg="6"
                              :selectXl="7"
                              :selectLg="5"
                              id="bearingMovement"
                              label="BRG.06 Bearing Movement?"
                              :refTable="REFERENCE_TABLE.BEARING_MOVEMENT"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.bearingMovement"
                              isRequired
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col lg="12" xl="12" class="pt-2 pl-1">
                            <LabelText
                              labelXl="11"
                              labelLg="12"
                              textXl="11"
                              textLg="12"
                              type="textarea"
                              label="BRG.12 Bearing Notes"
                              id="bearingNotes"
                              v-model="item.bearingComment"
                              :isEditable="isEditable && !item.readOnly"
                              :counter="bearingCommentMaxLength"
                              :maxlength="bearingCommentMaxLength"
                              @update:modelValue="changesDetected = true"
                            />
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col lg="6" xl="6" class="pa-0">
                            <LabelText
                              :labelXl="4"
                              :labelLg="6"
                              :textXl="7"
                              :textLg="5"
                              id="installYear"
                              label="BRG.13 Install Year"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.installDate"
                              type="number"
                              :maxlength="4"
                              :showComma="false"
                              @update:modelValue="changesDetected = true"
                              :rules="
                                isEditable
                                  ? [
                                      (value) => {
                                        return valueInRangeRule(value, 1901, 2999);
                                      },
                                    ]
                                  : []
                              "
                            ></LabelText>
                          </v-col>
                          <v-col lg="6" xl="6" class="pa-0">
                            <LabelSelect
                              :labelXl="4"
                              :labelLg="6"
                              :selectXl="7"
                              :selectLg="5"
                              id="replacementReason"
                              label="BRG.15 Replacement Reason"
                              :isRequired="item.isReplacing"
                              :refTable="REFERENCE_TABLE.BEARING_REPLACEMENT_REASON"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.replacementReason"
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col lg="6" xl="6" class="pa-0">
                            <LabelText
                              :labelXl="4"
                              :labelLg="6"
                              :textXl="7"
                              :textLg="5"
                              id="ecmsId"
                              label="BRG.14 ECMS ID"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.ecmsId"
                              type="number"
                              :maxlength="6"
                              :showComma="false"
                              @update:modelValue="changesDetected = true"
                            ></LabelText>
                          </v-col>
                        </v-row>
                        <v-row>
                          <v-col lg="12" xl="12" class="pt-2 pl-1">
                            <LabelText
                              labelXl="11"
                              labelLg="11"
                              textXl="12"
                              textLg="12"
                              type="textarea"
                              label="BRG.16 Replacement Notes"
                              id="replacementNotes"
                              v-model="item.replacementComment"
                              :isEditable="isEditable && !item.readOnly"
                              :counter="bearingCommentMaxLength"
                              :maxlength="bearingCommentMaxLength"
                              @update:modelValue="changesDetected = true"
                            />
                          </v-col>
                        </v-row>
                      </v-form>
                      <v-form ref="detailForm" v-model="validDetails" class="mt-2" v-else>
                        <v-row>
                          <v-col cols="12" class="pa-0">
                            <LabelText
                              :labelMd="4"
                              :labelSm="4"
                              :textSm="8"
                              :textMd="8"
                              id="bearingId"
                              label="BRG.01 Bearing Key"
                              v-model="item.bearingId"
                              @update:modelValue="changesDetected = true"
                            >
                            </LabelText>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelText
                              :labelMd="4"
                              :labelSm="4"
                              :textSm="8"
                              :textMd="8"
                              id="recordKey"
                              label="BRG.02 Record Key"
                              v-model="item.recordKey"
                              @update:modelValue="changesDetected = true"
                            >
                            </LabelText>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelSelect
                              :labelMd="4"
                              :labelSm="4"
                              :selectMd="8"
                              :selectSm="8"
                              id="bearingType"
                              label="BRG.03 Bearing Type"
                              :refTable="REFERENCE_TABLE.BEARING_TYPE"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.bearingType"
                              isRequired
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <v-row
                              ><v-col cols="8" class="pa-0">
                                <LabelSelect
                                  class="bearings-location"
                                  :labelMd="6"
                                  :labelSm="6"
                                  :selectMd="6"
                                  :selectSm="6"
                                  id="bearingLocation"
                                  label="BRG.04 Bearing Location"
                                  :options="bearingsValue.bearingLocations"
                                  :isEditable="
                                    (isEditable && !item.readOnly && !item.isAdd && !isReplacement(item)) ||
                                    (isEditable && item.isAdd && !item.isReplacing)
                                  "
                                  v-model="item.bearingLocation"
                                  isRequired
                                  @update:modelValue="changesDetected = true"
                                ></LabelSelect> </v-col
                              ><v-col cols="4" class="pa-0">
                                <LabelSelect
                                  :selectMd="12"
                                  :selectSm="12"
                                  id="bearingSubUnit"
                                  label=""
                                  :refTable="REFERENCE_TABLE.BEARING_SUBSTRUCTURE_UNIT"
                                  :isEditable="
                                    (isEditable && !item.readOnly && !item.isAdd && !isReplacement(item)) ||
                                    (isEditable && item.isAdd && !item.isReplacing)
                                  "
                                  v-model="item.bearingSubstrUnit"
                                  isRequired
                                  @update:modelValue="changesDetected = true"
                                ></LabelSelect>
                              </v-col>
                            </v-row>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelText
                              :labelMd="4"
                              :labelSm="4"
                              :textSm="5"
                              :textMd="4"
                              id="bearingCount"
                              label="BRG.05 Bearing Count"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.bearingCount"
                              :isRequired="isEditable && !item.readOnly"
                              type="number"
                              maxlength="3"
                              @update:modelValue="changesDetected = true"
                              :rules="
                                isEditable
                                  ? [
                                      (value) => {
                                        return valueInRangeRule(value, 1, 999);
                                      },
                                    ]
                                  : []
                              "
                            ></LabelText>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelSelect
                              :labelMd="4"
                              :labelSm="4"
                              :selectMd="8"
                              :selectSm="8"
                              id="bearingMovement"
                              label="BRG.06 Bearing Movement?"
                              :refTable="REFERENCE_TABLE.BEARING_MOVEMENT"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.bearingMovement"
                              isRequired
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelSelect
                              :labelMd="4"
                              :labelSm="4"
                              :selectMd="8"
                              :selectSm="8"
                              id="corrosion"
                              label="BRG.07 Corrosion?"
                              :showColon="false"
                              :refTable="REFERENCE_TABLE.BEARING_CORROSION"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.corrosion"
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>

                          <v-col cols="12" class="pa-0">
                            <LabelSelect
                              :labelMd="4"
                              :labelSm="4"
                              :selectMd="8"
                              :selectSm="8"
                              id="alignIssues"
                              label="BRG.08 Alignment Issues?"
                              :showColon="false"
                              :refTable="REFERENCE_TABLE.BEARING_ALIGNMENT_ISSUES"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.alignmentIssues"
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>

                          <v-col cols="12" class="pa-0">
                            <LabelSelect
                              :labelMd="4"
                              :labelSm="4"
                              :selectMd="8"
                              :selectSm="8"
                              id="boltIssues"
                              label="BRG.09 Anchor Bolt Issues?"
                              :showColon="false"
                              :refTable="REFERENCE_TABLE.BEARING_ANCHOR_BOLT"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.anchorBolt"
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>

                          <v-col cols="12" class="pa-0">
                            <LabelSelect
                              :labelMd="4"
                              :labelSm="4"
                              :selectMd="8"
                              :selectSm="8"
                              id="bearingAreaLoss"
                              label="BRG.10 Loss of Bearing Area?"
                              :showColon="false"
                              :refTable="REFERENCE_TABLE.BEARING_LOSS_AREA"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.bearingArea"
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>

                          <v-col cols="12" class="pa-0">
                            <LabelSelect
                              :labelMd="4"
                              :labelSm="4"
                              :selectMd="8"
                              :selectSm="8"
                              id="bearingCondRating"
                              label="BRG.11 Condition Rating"
                              :refTable="REFERENCE_TABLE.BEARING_CONDITION_RATING"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.conditionRating"
                              isRequired
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>

                          <v-col cols="12" class="pa-0">
                            <LabelText
                              labelMd="12"
                              labelSm="12"
                              textMd="12"
                              textSm="12"
                              type="textarea"
                              label="BRG.12 Bearing Notes"
                              id="bearingNotes"
                              v-model="item.bearingComment"
                              :isEditable="isEditable && !item.readOnly"
                              :counter="bearingCommentMaxLength"
                              :maxlength="bearingCommentMaxLength"
                              @update:modelValue="changesDetected = true"
                            />
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelText
                              :labelMd="4"
                              :labelSm="4"
                              :textSm="5"
                              :textMd="4"
                              id="installYear"
                              label="BRG.13 Install Year"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.installDate"
                              type="number"
                              :maxlength="4"
                              :showComma="false"
                              @update:modelValue="changesDetected = true"
                              :rules="
                                isEditable
                                  ? [
                                      (value) => {
                                        return valueInRangeRule(value, 1901, 2999);
                                      },
                                    ]
                                  : []
                              "
                            ></LabelText>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelText
                              :labelMd="4"
                              :labelSm="4"
                              :textSm="5"
                              :textMd="4"
                              id="ecmsId"
                              label="BRG.14 ECMS ID"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.ecmsId"
                              type="number"
                              :maxlength="6"
                              :showComma="false"
                              @update:modelValue="changesDetected = true"
                            ></LabelText>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelSelect
                              :labelMd="4"
                              :labelSm="4"
                              :selectMd="8"
                              :selectSm="8"
                              id="replacementReason"
                              label="BRG.15 Replacement Reason"
                              :isRequired="item.isReplacing"
                              :refTable="REFERENCE_TABLE.BEARING_REPLACEMENT_REASON"
                              :isEditable="isEditable && !item.readOnly"
                              v-model="item.replacementReason"
                              @update:modelValue="changesDetected = true"
                            ></LabelSelect>
                          </v-col>
                          <v-col cols="12" class="pa-0">
                            <LabelText
                              labelMd="12"
                              labelSm="12"
                              textMd="12"
                              textSm="12"
                              type="textarea"
                              label="BRG.16 Replacement Notes"
                              id="replacementNotes"
                              v-model="item.replacementComment"
                              :isEditable="isEditable && !item.readOnly"
                              :counter="bearingCommentMaxLength"
                              :maxlength="bearingCommentMaxLength"
                              @update:modelValue="changesDetected = true"
                            />
                          </v-col>
                        </v-row>
                      </v-form>
                    </td>
                  </template>

                  <template v-slot:bottom>
                    <DataTableFooter
                      ref="bearingsFooter"
                      id="inspection_bearings_footer"
                      :items="bearings"
                      v-model="pageInspection"
                    ></DataTableFooter>
                  </template> </v-data-table></v-col
            ></v-row>
          </v-form>
        </v-expansion-panel-text>
      </v-expansion-panel> </v-expansion-panels
  ></v-row>
  <ConfirmDialog ref="closeConfirm" :cancelButtonText="BUTTONS.NO" />
</template>

<script>
import { ref, computed, watch, nextTick, onMounted } from "vue";
import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { useInspectionStore } from "@/stores/inspection";
import LabelSelect from "@/components/common/LabelSelect.vue";
import LabelText from "@/components/common/LabelText.vue";
import CommentTypeTextField from "@/components/common/CommentTypeTextField.vue";
import { INSPECTION_COMMENTS } from "@/constants/Inspections";
import { clone } from "@/util/clone";
import IconTooltip from "@/components/common/IconTooltip.vue";
import { useConfigStore } from "@/stores/config";
import { convertToInt } from "@/composables/util";
import { ENV_CONFIG_PROPERTY } from "@/constants/EnvConfigProperties";
import { LOGGER } from "@/util/logger";
import ConfirmDialog from "@/components/common/ConfirmDialog.vue";
import { INSPECTION_MESSAGES, MODULE_TYPES } from "@/constants/InspectionConstants";
import { BUTTONS } from "@/constants/CommonWebConstants";
import { bearingDetail } from "@/data/bearing";
import DataTableHeader from "@/components/common/DataTableHeader.vue";
import { valueInRangeRule } from "@/composables/validationRules";
import DataTableFooter from "@/components/common/DataTableFooter.vue";
import { scrollToDataTable } from "@/util/scrollToElement";
import ExpandCollapseToggle from "@/components/shared/ExpandCollapseToggle.vue";
import { useSnackbarStore } from "@/stores/snackbar";
import { useBearingsStore } from "@/components/inspection/store/bearings";
import { useQuickSearchStore } from "@/stores/quickSearch";
import { storeToRefs } from "pinia";
import ManagementCommentTypeTextField from "@/components/common/ManagementCommentTypeTextField.vue";
import { useInspectionLinksStore } from "@/stores/inspectionLinks";
import { useUserPermissionStore } from "@/stores/userPermission";
import { INSPECTION_LINKS_BEARINGS_PERMISSIONS } from "@/rbac";
import { INSPECTION_STATUSES } from "@/constants/InspectionStatuses";
import { ACCESS_LEVELS } from "@/rbac/accessLevels.js";
import { SNACKBAR_MARKER } from "@/constants/GlobalSnackbar";
import ValidationCountsHeader from "@/components/shared/ValidationCountsHeader.vue";

export default {
  name: "InspectionBearings",
  props: {
    moduleType: { default: MODULE_TYPES.INSPECTION },
  },
  data() {
    return {
      headers: [
        {
          key: "data-table-expand",
          width: "1%",
        },
        {
          line1: "BRG.01",
          line2: "Bearing",
          line3: "Key",
          key: "bearingId",
          sortable: true,
          width: "9%",
        },
        {
          line1: "BRG.02",
          line2: "Record",
          line3: "Key",
          key: "recordKey",
          sortable: false,
          width: "4%",
        },
        {
          line1: "BRG.03",
          line2: "Bearing",
          line3: "Type",
          key: "bearingType",
          width: "9%",
          sortable: false,
        },
        {
          line1: "BRG.04",
          line2: "Bearing",
          line3: "Location",
          key: "bearingLocation",
          sortable: false,
          width: "8%",
        },
        {
          line1: "BRG.05",
          line2: "Bearing",
          line3: "Count",
          key: "bearingCount",
          sortable: false,
          width: "8%",
        },
        {
          line1: "BRG.06",
          line2: "Bearing",
          line3: "Movement?",
          key: "bearingMovement",
          sortable: false,
          width: "8%",
        },
        {
          line1: "BRG.07",
          line2: "Corrosion?",
          line3: null,
          key: "corrosion",
          sortable: false,
          width: "8%",
        },
        {
          line1: "BRG.08",
          line2: "Alignment",
          line3: "Issues?",
          key: "alignmentIssues",
          width: "8%",
          sortable: false,
        },
        {
          line1: "BRG.09",
          line2: "Anchor",
          line3: "Bolt Issues?",
          key: "anchorBolt",
          width: "8%",
          sortable: false,
        },
        {
          line1: "BRG.10",
          line2: "Loss of",
          line3: "Bearing Area?",
          key: "bearingArea",
          width: "8%",
          sortable: false,
        },
        {
          line1: "BRG.11",
          line2: "Condition",
          line3: "Rating",
          key: "conditionRating",
          sortable: true,
          width: "10%",
        },

        {
          line1: null,
          line2: "Action",
          line3: null,
          key: "action",
          sortable: false,
          width: "3%",
        },
      ],
    };
  },
  setup(props) {
    const snackbarStore = useSnackbarStore();
    const allPanels = [0];
    const inspectionStore = useInspectionStore();
    const inspectionLinksStore = useInspectionLinksStore();
    const userPermissionStore = useUserPermissionStore();
    const quickSearchStore = useQuickSearchStore();
    const { hasQuickSearchSucceeded } = storeToRefs(quickSearchStore);
    let panel = ref(allPanels);
    let bearingsStore = useBearingsStore();
    let snackBarId = ref("");
    let structure = computed(() => inspectionStore.selectedInspection);

    const isEditable = computed(() => {
      if (props.moduleType == MODULE_TYPES.MANAGEMENT) {
        return hasQuickSearchSucceeded.value && bearingsValue?.value?.brkey && hasAccessToEdit.value;
      } else {
        return inspectionStore.isEditable;
      }
    });

    const hasAccessToEdit = computed({
      get() {
        if (structureHeader.value?.INSPECTIONSTATUS === INSPECTION_STATUSES.ACCEPTED) {
          return userPermissionStore.checkUserPermission(
            INSPECTION_LINKS_BEARINGS_PERMISSIONS[ACCESS_LEVELS.CUSTOM.EDIT_APPROVED_INSPECTION],
            userPermissionStore.getUserPermissionByBrkeyFromCache(bearingsValue?.value?.brkey)
          );
        } else {
          return userPermissionStore.checkUserPermission(
            INSPECTION_LINKS_BEARINGS_PERMISSIONS[ACCESS_LEVELS.EDIT],
            userPermissionStore.getUserPermissionByBrkeyFromCache(bearingsValue?.value?.brkey)
          );
        }
      },
    });
    const bearingCommentMaxLength = computed(() => {
      return configStore.getEnvConfigValue(ENV_CONFIG_PROPERTY.BEARING_COMMENT_MAX_LENGTH);
    });
    const inspCommentMaxLength = computed(() => {
      return configStore.getEnvConfigValue(ENV_CONFIG_PROPERTY.INSPECTION_COMMENT_MAX_LENGTH);
    });

    let bearings = ref([]);
    let expandedRows = ref([]);
    let validDetails = ref(false);
    let brkey = ref("");
    let inspectionId = ref("");
    let resetDirty = ref(false);
    let bearingsValue = ref({});
    const structureHeader = computed(() => quickSearchStore.getStructure());
    const configStore = useConfigStore();
    const valid = ref(true);

    const bearingConditions = ref(null);

    let showAllBearings = ref(false);

    const readData = async () => {
      bearingsValue.value = {};
      resetDirty.value = true;
      brkey.value = "";
      inspectionId.value = "";
      if (props.moduleType == MODULE_TYPES.MANAGEMENT && structureHeader.value?.BRKEY) {
        if (!hasQuickSearchSucceeded.value) {
          resetDirty.value = true;
          return;
        }
        await bearingsStore.loadDataObject(
          props.moduleType,
          structureHeader.value.BRKEY,
          structureHeader.value.INSPECTIONID
        );
        bearingsValue.value = bearingsStore.getBearings();
      } else if (props.moduleType == MODULE_TYPES.INSPECTION) {
        brkey.value = structure.value.structureBridge.brkey;
        inspectionId.value = structure.value.inspection.inspectionId;
        await bearingsStore.loadDataObject(props.moduleType, null, null, structure.value);
        bearingsValue.value = bearingsStore.getBearings();
      }
      getBearingDetails();
      resetDirty.value = true;
      await validate();
    };
    const getNewBearingDetail = () => {
      return clone(bearingDetail);
    };

    let detailForm = ref(null);
    let changesDetected = ref(false);
    const closeConfirm = ref(null);
    let bearingsFooter = ref(null);
    const pageInspection = ref({
      itemsPerPage: 30,
      page: 1,
    });
    let previousSort = ref([{ key: "bearingId", order: "asc" }]);
    let sortBy = ref([{ key: "bearingId", order: "asc" }]);
    async function validate() {
      bearingConditions.value.validate();
    }
    onMounted(async () => {
      await readData();
    });
    const onRowExpand = (event) => {
      //on clicking on row expansion icon - set the data to readonly.
      event.data.readOnly = true;
    };

    function getBearingLocation(value) {
      if (
        bearingsValue.value.bearingLocations?.find((a) => a.value === value.bearingLocation)?.title &&
        configStore.getReferenceValue(REFERENCE_TABLE.BEARING_SUBSTRUCTURE_UNIT, value.bearingSubstrUnit)?.split(" ")[2]
      ) {
        return (
          bearingsValue.value.bearingLocations?.find((a) => a.value === value.bearingLocation)?.title +
          "-" +
          configStore
            .getReferenceValue(REFERENCE_TABLE.BEARING_SUBSTRUCTURE_UNIT, value.bearingSubstrUnit)
            .split(" ")[2]
        );
      } else {
        return "";
      }
    }

    const sort = (arr) => {
      arr.value = arr.value?.sort(function (a, b) {
        if (a.bearingId == b.bearingId) {
          return convertToInt(b.recordKey) - convertToInt(a.recordKey);
        } else {
          return a.bearingId - b.bearingId;
        }
      });
    };

    const isNotCurrent = (details) => {
      //check for replacements
      const maxRecordkey = Math.max(
        ...bearings.value.filter((a) => a.bearingId == details.bearingId).map((a) => a.recordKey)
      );
      return details.recordKey != maxRecordkey;
    };

    const isReplacement = (details) => {
      //check if if replacement
      const minRecordkey = Math.min(
        ...bearings.value.filter((a) => a.bearingId == details.bearingId).map((a) => a.recordKey)
      );
      return details.recordKey != minRecordkey;
    };

    const cloneCurrentInspectionBearings = () => {
      let bearingsClone = clone(bearingsValue.value?.structureBearings);

      if (!bearingsClone) {
        //remove null values from array(if any)
        bearingsClone = bearingsClone?.filter((a) => !!a);
      }

      return bearingsClone;
    };
    const getAllBearingDetails = () => {
      expandedRows.value = [];
      bearings.value = cloneCurrentInspectionBearings();
      //set unique key for toggle expansion
      for (const element of bearings.value ?? []) {
        element.uniqueKey = element.bearingId + "-" + element.recordKey;
        element.readOnly = true;
      }
      sort(bearings);
    };

    const getActiveBearingDetails = () => {
      expandedRows.value = [];
      bearings.value = cloneCurrentInspectionBearings();
      let activeBearings = [];

      //set unique key for toggle expansion
      if (bearings.value) {
        for (const element of bearings.value ?? []) {
          if (element?.bearingId && element?.recordKey) {
            element.uniqueKey = element?.bearingId + "-" + element?.recordKey;
            element.readOnly = true;
            if (!isNotCurrent(element)) {
              activeBearings.push(element);
            }
          }
        }
      }
      bearings.value = activeBearings;
      sort(bearings);
    };

    const saveChanges = async () => {
      const isSuccess = await bearingsStore.saveData(props.moduleType, bearingsValue.value, structure.value);
      if (isSuccess) {
        inspectionLinksStore.isDirty = false;
        if (props.moduleType == MODULE_TYPES.INSPECTION) {
          await bearingsStore.loadDataObject(props.moduleType, null, null, structure.value);
        }
        bearingsValue.value = bearingsStore.getBearings();
        getBearingDetails();
        // Using this flag to not set the dirty flag in watch
        resetDirty.value = true;
      }
      return isSuccess;
    };

    const hasReplacedBearingDetails = () => {
      const clonedBearings = cloneCurrentInspectionBearings();

      if (clonedBearings) {
        const hasReplacedBearingsDetailsValue = clonedBearings.some(
          (element) => element.bearingId && element.recordKey && isNotCurrent(element)
        );
        if (!hasReplacedBearingsDetailsValue) {
          showAllBearings.value = false;
        }
        return hasReplacedBearingsDetailsValue;
      }
      showAllBearings.value = false;
      return false;
    };

    const addNewBearing = () => {
      inspectionStore.setAddingOrEditing(true);
      snackBarId.value = snackbarStore.showMessage({
        displayText: INSPECTION_MESSAGES.BEARINGS_DATATABLE,
      });
      let newBearing = getNewBearingDetail();
      isEditing.value = true;
      newBearing.brkey = bearingsValue.value.brkey;
      newBearing.inspectionId = bearingsValue.value.inspectionId;
      newBearing.bearingId = getNextBearingKey() + 1;
      newBearing.recordKey = "1";
      newBearing.isAdd = true;
      newBearing.uniqueKey = newBearing.bearingId + "-" + newBearing.recordKey;
      newBearing.readOnly = false;
      bearings.value.unshift(newBearing);
      previousSort.value = sortBy.value.map((x) => x);
      sortBy.value = [];
      expandedRows.value = [newBearing];
      scrollToDataTable("bearings-data-table");

      bearingsFooter.value?.setPageValue(1);
      nextTick(() => {
        detailForm.value.validate();
      });
    };

    const closeDetail = () => {
      if (isEditable.value && changesDetected.value) {
        //show confirmation dialog on closing
        closeConfirm.value
          .open(INSPECTION_MESSAGES.DETECT_DATATABLE_CHANGE_CONFIRMATION_MESSAGE, BUTTONS.YES)
          .then((response) => {
            if (!response) {
              changesDetected.value = false;
              getBearingDetails();
              expandedRows.value = [];
              inspectionStore.setAddingOrEditing(false);
              snackbarStore.removeMessage(snackBarId.value);
              sortBy.value = previousSort.value?.map((x) => x);
              isEditing.value = false;
            }
          });
      } else {
        getBearingDetails();
        isEditing.value = false;
        expandedRows.value = [];
        inspectionStore.setAddingOrEditing(false);
        snackbarStore.removeMessage(snackBarId.value);
        sortBy.value = previousSort.value?.map((x) => x);
      }
    };

    let validationSnackbar = computed({
      get() {
        if (props.moduleType === MODULE_TYPES.MANAGEMENT) {
          return inspectionLinksStore.showValidationSnackbar;
        } else {
          return inspectionStore.getShowValidationSnackbar;
        }
      },
      set(value) {
        if (props.moduleType === MODULE_TYPES.MANAGEMENT) {
          inspectionLinksStore.showValidationSnackbar = value;
        } else {
          inspectionStore.setShowValidationSnackbar(value);
        }
      },
    });

    const saveDetail = (detail) => {
      detailForm.value
        .validate()
        .then(() => {
          if (validDetails.value) {
            validationSnackbar.value = false;
            if (!bearingsValue.value?.structureBearings?.[0]) {
              //remove null values from array(if any)
              bearingsValue.value.structureBearings = bearingsValue.value.structureBearings?.filter((a) => !!a);
            }

            changesDetected.value = false;
            if (detail.isAdd) {
              delete detail.isAdd;
              bearingsValue.value.structureBearings.push(detail);
            } else {
              //update existing detail
              const updateIndex = bearingsValue.value.structureBearings.findIndex(
                (a) => a.bearingId === detail.bearingId && a.recordKey === detail.recordKey
              );
              bearingsValue.value.structureBearings.splice(updateIndex, 1, detail);
            }

            //if all required fields entered - close the expanded row
            expandedRows.value = [];
            isEditing.value = false;
            inspectionStore.setAddingOrEditing(false);
            snackbarStore.removeMessage(snackBarId.value);
            getBearingDetails();
            sortBy.value = previousSort.value?.map((x) => x);
          } else {
            validationSnackbar.value = true;
          }
        })
        .catch((err) => {
          LOGGER.logException(err);
          changesDetected.value = false;
        });
    };

    const editDetail = (detail) => {
      previousSort.value = sortBy.value.map((x) => x);

      inspectionStore.setAddingOrEditing(true);
      snackBarId.value = snackbarStore.showMessage({
        displayText: INSPECTION_MESSAGES.BEARINGS_DATATABLE,
      });
      isEditing.value = true;
      detail.readOnly = false;
      //if all required fields entered - close the expanded row
      expandedRows.value = [detail];
      nextTick(() => {
        detailForm.value.validate();
      });
      sortBy.value = [];
      moveEditableElementToTop(detail);
      bearingsFooter.value?.setPageValue(1);
    };

    const moveEditableElementToTop = (detail) => {
      const index = bearings.value.findIndex(
        (a) => a.bearingId === detail.bearingId && a.recordKey === detail.recordKey
      );
      let editableElement = bearings.value[index];
      //then remove that element and use unshift
      bearings.value.splice(index, 1);
      bearings.value.unshift(editableElement);
    };

    const replaceDetail = (detail) => {
      inspectionStore.setAddingOrEditing(true);
      snackBarId.value = snackbarStore.showMessage({
        displayText: INSPECTION_MESSAGES.BEARINGS_DATATABLE,
      });
      isEditing.value = true;
      previousSort.value = sortBy.value.map((x) => x);
      let newBearing = getNewBearingDetail();
      newBearing.brkey = bearingsValue.value.brkey;
      newBearing.inspectionId = bearingsValue.value.inspectionId;
      newBearing.isAdd = true;
      newBearing.isReplacing = true;
      newBearing.bearingId = detail.bearingId;
      newBearing.recordKey = (getNextRecordKey(detail.bearingId) + 1).toString();
      newBearing.bearingSubstrUnit = detail.bearingSubstrUnit;
      newBearing.bearingLocation = detail.bearingLocation;
      newBearing.uniqueKey = newBearing.bearingId + "-" + newBearing.recordKey;
      newBearing.readOnly = false;
      bearings.value.push(newBearing);
      expandedRows.value = [newBearing];
      sort(bearings);
      nextTick(() => {
        detailForm.value.validate();
      });
    };

    const deleteDetail = (detail) => {
      const deleteIndex = bearingsValue.value.structureBearings?.findIndex(
        (a) => a.bearingId === detail.bearingId && a.recordKey === detail.recordKey
      );
      if (deleteIndex > -1) {
        bearingsValue.value.structureBearings.splice(deleteIndex, 1);
        expandedRows.value = [];
        getBearingDetails();
      }
    };

    const getNextBearingKey = () => {
      if (bearings.value) {
        return Math.max(...bearings.value.map((bearingDetail) => convertToInt(bearingDetail?.bearingId ?? 0)), 0);
      } else {
        return 0;
      }
    };

    const getNextRecordKey = (bearingId) => {
      let bearingDetails = bearings.value.filter((bearingDetail) => bearingDetail.bearingId == bearingId);
      return Math.max(...bearingDetails.map((bearingDetail) => convertToInt(bearingDetail.recordKey || 0)));
    };

    const isExpanded = (details) => {
      return (
        expandedRows.value?.findIndex((a) => a.bearingId == details.bearingId && a.recordKey === details.recordKey) >= 0
      );
    };

    const rowClass = (data) => {
      return isNotCurrent(data) && !data.isAdd ? "rowReplaced" : "rowCurrent";
    };

    const anyBearingDetailEditable = computed(() => {
      return expandedRows.value?.find((element) => !element.readOnly) != null;
    });
    const getBearingDetails = () => {
      if (showAllBearings.value) {
        getAllBearingDetails();
      } else {
        getActiveBearingDetails();
      }
    };

    const setUpdatedComment = (comment, commentType) => {
      const foundComment = bearingsValue.value.inspectionComments.find((c) => {
        return c.commentType === commentType;
      });
      if (foundComment) {
        foundComment.notes = comment.notes;
      } else {
        bearingsValue.value.inspectionComments.push(comment);
      }
    };

    let isEditing = computed({
      get() {
        return inspectionLinksStore.isAddingOrEditingBearings;
      },
      set(value) {
        inspectionLinksStore.isAddingOrEditingBearings = value;
      },
    });

    const toggleRow = (detail) => {
      if (expandedRows.value.includes(detail)) {
        const index = expandedRows.value.findIndex((d) => d.uniqueKey == detail.uniqueKey);
        expandedRows.value.splice(index, 1);
      } else {
        expandedRows.value.push(detail);
      }
    };

    const toggleAllPanels = (expandAll) => {
      if (expandAll) {
        panel.value = allPanels;
      } else {
        panel.value = [];
      }
    };

    const cancelChanges = async () => {
      //only called from Inspection Links
      bearingsValue.value = bearingsStore.getBearings();
      getBearingDetails();

      // Using this flag to not set the dirty flag in watch
      resetDirty.value = true;
      inspectionLinksStore.isDirty = false;
    };

    const printD491 = () => {
      return { reportPage: "D491-Insp-Bearings-Screen", marker: SNACKBAR_MARKER.BEARINGS };
    };

    watch(
      [bearingsValue],
      () => {
        // This watch will NOT be triggered during initial onMounted()
        if (props.moduleType === MODULE_TYPES.MANAGEMENT) {
          // for management module
          if (resetDirty.value) {
            inspectionLinksStore.isDirty = false;
            resetDirty.value = false;
          } else {
            inspectionLinksStore.isDirty = true;
          }
        } else if (props.moduleType === MODULE_TYPES.INSPECTION) {
          // for inspection  module
          if (resetDirty.value) {
            inspectionStore.setDirtyFlag(false);
            resetDirty.value = false;
          } else {
            inspectionStore.setDirtyFlag(true);
          }
        }
      },
      { deep: true }
    );

    // watch those objects used in component from structure to warn about unsaved changes
    watch(
      () => [props],
      () => {
        inspectionStore.setDirtyFlag(true);
      },
      { deep: true }
    );

    return {
      isReplacement,
      getBearingLocation,
      panel,
      readData,
      REFERENCE_TABLE,
      isEditable,
      INSPECTION_COMMENTS,
      bearings,
      onRowExpand,
      expandedRows,
      addNewBearing,
      validDetails,
      isExpanded,
      configStore,
      closeDetail,
      saveDetail,
      editDetail,
      replaceDetail,
      deleteDetail,
      bearingCommentMaxLength,
      inspCommentMaxLength,
      rowClass,
      anyBearingDetailEditable,
      detailForm,
      changesDetected,
      getAllBearingDetails,
      getActiveBearingDetails,
      showAllBearings,
      isNotCurrent,
      closeConfirm,
      BUTTONS,
      inspectionStore,
      INSPECTION_MESSAGES,
      getBearingDetails,
      toggleRow,
      hasReplacedBearingDetails,
      valueInRangeRule,
      pageInspection,
      bearingsFooter,
      sortBy,
      bearingConditions,
      valid,
      validate,
      toggleAllPanels,
      setUpdatedComment,
      MODULE_TYPES,
      structure,
      bearingsValue,
      saveChanges,
      printD491,
      cancelChanges,
    };
  },
  components: {
    DataTableHeader,
    LabelSelect,
    CommentTypeTextField,
    LabelText,
    IconTooltip,
    ConfirmDialog,
    DataTableFooter,
    ExpandCollapseToggle,
    ManagementCommentTypeTextField,
    ValidationCountsHeader,
  },
};
</script>
<style scoped lang="scss">
@use "@/styles/colors" as c;
.alignCenter {
  text-align: center;
}
.v-data-table :deep(button:hover) {
  background-color: inherit !important;
  color: inherit !important;
}

.v-data-table :deep(button:focus) {
  outline: none !important;
}
.tableHeader {
  text-align: center !important;
  background: c.$p-pa-light-blue !important;
}
</style>
